import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useReducer, useState } from 'react';
import { t } from 'i18next';
import { IProductViewModel } from 'Interfaces/IProductConfig';
import {
  EnumOrderPostStatus,
  EnumProductVariant,
} from 'Interfaces/IGlobalTypeConfig';
import useWindowsDimension from 'functions/useWindowsDimension';
import { rq } from 'http/apiRoutes';
import { useGet } from 'http/useApi';

import { IEmployeeViewModel } from 'Interfaces/IEmployeeConfig';
import { useAtom } from 'jotai';
import userAtom from 'data/atoms/userAtom';
import { returnGuidEmpty } from 'functions/GuidChecks';
import { useQueryClient } from 'react-query';
import { SingleInvestmentProductView } from '../SingleInvestmentProductView';
import { OrderPostCompanyViewModel, OrderPostProductViewModel, OrderPostTicketContactInfoViewModel } from 'pages/Orders/ViewModels/OrderPostViewModels';

// ---- Local state/reducer ----
interface State {
  catalogue: IProductViewModel[];
  selected: OrderPostProductViewModel[];
  totalItems: number;
}

type Action =
  | {
      type: 'SET_PAGE_DATA';
      payload: { products: IProductViewModel[]; total: number };
    }
  | { type: 'ADD_SELECTED'; payload: OrderPostProductViewModel }
  | { type: 'REMOVE_SELECTED'; payload: { index: number } }
  | {
      type: 'UPDATE_SELECTED';
      payload: {
        index: number;
        field: keyof OrderPostProductViewModel;
        value: any;
      };
    }
  | { type: 'CLEAR_SELECTED' };

const initialState: State = {
  catalogue: [],
  selected: [],
  totalItems: 0,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_PAGE_DATA':
      return {
        ...state,
        catalogue: action.payload.products,
        totalItems: action.payload.total,
      };
    case 'ADD_SELECTED':
      return { ...state, selected: [...state.selected, action.payload] };
    case 'REMOVE_SELECTED': {
      const next = [...state.selected];
      next.splice(action.payload.index, 1);
      return { ...state, selected: next };
    }
    case 'UPDATE_SELECTED': {
      const next = [...state.selected];
      const item = { ...next[action.payload.index] } as any;
      item[action.payload.field] = action.payload.value;
      next[action.payload.index] = item;
      return { ...state, selected: next };
    }
    case 'CLEAR_SELECTED':
      return { ...state, selected: [] };
    default:
      return state;
  }
};

type Props = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCommitToTicket: (items: OrderPostProductViewModel[]) => void; // NY
  companyId: string;
  ticketId?: string;
};

export const TicketProductDrawer = ({
  open,
  onOpenChange,
  onCommitToTicket,
  companyId,
  ticketId,
}: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { height } = useWindowsDimension();
  const [user] = useAtom(userAtom);
  const queryClient = useQueryClient();

  // paging/søk
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState<boolean>(!!open);

  // fetch katalog (Investment) når drawer er åpen
  const isGet = drawerOpen;
  const { data: getProducts } = useGet(
    rq.getProductByType(
      EnumProductVariant.Investment,
      page,
      pageSize,
      isGet /* , search om backenden støtter */
    ),
    
  );

  const makeHandlers = (index: number) => ({
    onQuantityChange: (value: number) =>
      dispatch({
        type: 'UPDATE_SELECTED',
        payload: { index, field: 'quantity', value },
      }),
    onRetailPriceChange: (value: number) =>
      dispatch({
        type: 'UPDATE_SELECTED',
        payload: { index, field: 'retailPrice', value },
      }),
    onVendorUrlChange: (value: string) =>
      dispatch({
        type: 'UPDATE_SELECTED',
        payload: { index, field: 'vendorProductUrl', value },
      }),
    onOwnerChange: (owner: IEmployeeViewModel | null) => {
      const ownerVm = owner
        ? ({
            id: owner.id,
            name: owner.name,
            phone: owner.phone,
            email: owner.email,
            url: owner.url,
          } as OrderPostTicketContactInfoViewModel)
        : null;
      dispatch({
        type: 'UPDATE_SELECTED',
        payload: { index, field: 'owner', value: ownerVm },
      });
    },
    onVendorChange: (vendor: OrderPostCompanyViewModel | null) =>
      dispatch({
        type: 'UPDATE_SELECTED',
        payload: { index, field: 'vendor', value: vendor },
      }),
    onRemove: () => {
      dispatch({
        type: 'REMOVE_SELECTED',
        payload: { index },
      });
    },
  });

  useEffect(() => {
    if (typeof open === 'boolean') setDrawerOpen(open);
  }, [open]);

  const setOpen = (val: boolean) => {
    setDrawerOpen(val);
    onOpenChange?.(val);
  };

  useEffect(() => {
    if (getProducts) {
      dispatch({
        type: 'SET_PAGE_DATA',
        payload: {
          products: getProducts.products,
          total: getProducts.numberOfProducts,
        },
      });
    }
  }, [getProducts]);

  // map: IProductViewModel -> IInvestmentProductViewModel (defaultverdier)
  const toInvestmentVM = (p: IProductViewModel): OrderPostProductViewModel => ({
    id: p.id,
    orderPostId: returnGuidEmpty(),
    name: p.name,
    quantity: 1,
    orderedQuantity: 0,
    unitPrice: p.unitPrice || 0,
    retailPrice: p.retailPrice || 0,
    vendorProductUrl: '',
    status: EnumOrderPostStatus.WaitingToBeOrdered,
    purchaser:
      user && user.id
        ? {
            id: user.id,
            name: user.name,
            phone: user.phone,
            email: user.email,
            url: user.url,
          }
        : undefined,
    owner: null,
    vendor: null,
    url: p.url,
  });

  // map til backend sitt OrderPostProductViewModel
  const mapToOrderPost = (
    p: OrderPostProductViewModel
  ): OrderPostProductViewModel => ({
    id: p.id,
    orderPostId: p.orderPostId,
    name: p.name,
    quantity: p.quantity,
    orderedQuantity: p.orderedQuantity,
    unitPrice: p.unitPrice,
    retailPrice: p.retailPrice,
    vendorProductUrl: p.vendorProductUrl,
    purchaser: p.purchaser,
    owner: p.owner,
    vendor: p.vendor,
    status: p.status,
    url: p.url,
  });

  // ---- Lagring til backend på valgt ticket ----
  // Bytt til DIN faktiske route i rqPost:
  // f.eks.: rqPost.orderPostProducts(ticketId) eller rqPost.addOrderPostProducts(ticketId)
  //const postOrderProducts = usePost(rqPost.orderPostProducts(ticketId), !!ticketId);

  const handleSelect = (product: IProductViewModel) => {
    const vm = toInvestmentVM(product);
    dispatch({ type: 'ADD_SELECTED', payload: vm });
  };

  const handleSave = async () => {
    const payload = state.selected.map(mapToOrderPost); // map til backend-VM
    // console.log('NEWTICKET  handleSave, payload:', payload);
    await onCommitToTicket(payload);
    dispatch({ type: 'CLEAR_SELECTED' });

    // optimistic add
    queryClient.setQueryData(['ticketProducts', ticketId], (old: any = {}) => ({
      ...old,
      consumables: [...(old?.consumables || []), ...payload],
    }));

    // safety refetch after short delay
    await new Promise((r) => setTimeout(r, 400));
    await queryClient.refetchQueries(['ticketProducts', ticketId]);

    setOpen(false);
  };

  const renderCatalogue = (products: IProductViewModel[]) => (
    <>
      <List
        sx={{
          width: '100%',
          p: 2,
          height: height - 230,
          backgroundColor: 'primary.back',
          overflow: 'auto',
        }}
      >
        {products.map((p, idx) => (
          <Paper
            key={p.id || idx}
            sx={{
              m: 1,
              p: 0,
              ':hover': { cursor: 'pointer', filter: 'brightness(108%)' },
            }}
            onClick={() => handleSelect(p)}
          >
            <SingleInvestmentProductView
              product={toInvestmentVM(p)}
              index={idx}
              editable={false}
              companyId={companyId}
            />
          </Paper>
        ))}
      </List>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, gap: 2 }}>
        <Pagination
          count={Math.max(1, Math.ceil(state.totalItems / pageSize))}
          page={page}
          onChange={(_, value) => setPage(value)}
        />
        <Typography sx={{ display: 'flex', alignItems: 'center' }}>
          Items per page:
        </Typography>
        <Select
          size='small'
          variant='outlined'
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          sx={{ width: 85, mt: -1 }}
        >
          {[15, 25, 50, 100].map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </>
  );

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        height: 64,
      }}
    >
      <Typography variant='h6'>
        {t('ticketProductDrawer.listHeader') || 'Product catalogue'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          variant='outlined'
          size='small'
          placeholder={t('Common.search') || 'Search products'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {!open && (
        <Box sx={{ width: 32, height: 32 }}>
          <IconButton
            sx={{ p: 0, m: 0, width: 18, height: 18 }}
            onClick={() => setOpen(true)}
          >
            <AddRoundedIcon sx={{ fontSize: 26 }} />
          </IconButton>
        </Box>
      )}

      <Drawer
        anchor='left'
        open={drawerOpen}
        onClose={() => setOpen(false)}
        sx={{ width: '100%', flexShrink: 0 }}
        PaperProps={{ style: { width: '100%' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, p: 2 }}>
          {/* Venstre: katalog */}
          <Box sx={{ width: '45%' }}>
            {header}
            {renderCatalogue(state.catalogue)}
          </Box>

          {/* Midt: valgt liste (editerbar) */}
          <Stack
            sx={{
              width: '35%',
              display: 'flex',
              flexDirection: 'column',
              height: height - 28,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                height: 64,
              }}
            >
              <Typography variant='h6'>
                {t('ticketProductDrawer.investmentProduct')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}></Box>
            </Box>
            <List
              sx={{
                width: '100%',
                m: 0,
                p: 1,
                backgroundColor: 'primary.back',
                height: height - 218,
                overflow: 'auto',
              }}
            >
              {state.selected.map((product, index) => {
                const h = makeHandlers(index);
                return (
                  <Paper key={product.id || index} sx={{ p: 1 }}>
                    <SingleInvestmentProductView
                      product={product}
                      editable
                      onQuantityChange={h.onQuantityChange}
                      onRetailPriceChange={h.onRetailPriceChange}
                      onVendorUrlChange={h.onVendorUrlChange}
                      onOwnerChange={h.onOwnerChange}
                      onVendorChange={h.onVendorChange}
                      onRemove={h.onRemove}
                      companyId={companyId}
                    />
                  </Paper>
                );
              })}
            </List>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pt: 2,
                px: 2,
                mt: -1,
              }}
            >
              <Button
                variant='outlined'
                onClick={() => dispatch({ type: 'CLEAR_SELECTED' })}
              >
                {t('Ticket.TicketProductDrawer.clearProducts') || 'Clear'}
              </Button>
              <Button variant='contained' onClick={handleSave}>
                {t('Ticket.TicketProductDrawer.addProducts') || 'Add Products'}
              </Button>
            </Box>
          </Stack>

          {/* Høyre: kontroller */}
          <Stack
            sx={{
              width: '20%',
              display: 'flex',
              flexDirection: 'column',
              height: height - 28,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                height: 64,
                mb: 2,
              }}
            >
              <IconButton onClick={() => setOpen(false)}>
                <CloseRoundedIcon />
              </IconButton>
            </Box>
            <Box sx={{ px: 2 }}>
              <Typography variant='subtitle2' sx={{ letterSpacing: 1 }}>
                {t('ticketProductDrawer.investmentProductInfo') ||
                  'Edit quantity, price and vendor URL for each selected product before saving.'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};
